import { lerpColor } from '../../color';
import { calmAt, calmCenter, catenary, grain, rgba, spots, vignette, type Painter } from '../../art/paint';
import { hsl, type World } from '../../world';

const TAU = Math.PI * 2;

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly l: number;
  readonly a1: number;
  readonly a2: number;
}

interface Cake {
  readonly x: number;
  readonly base: number;
  readonly hw: number;
  readonly out: -1 | 1;
  readonly frost: number;
}

function tonesOf(look: World): Tones {
  const pal = look.palette;
  return {
    bg: pal.bg,
    far: hsl(look.hue, 36, 16),
    ceil: look.ceil,
    ink: pal.ink,
    rim: pal.rim,
    l: pal.incoming.circle,
    a1: pal.held,
    a2: pal.spot,
  };
}

function stick(ctx: CanvasRenderingContext2D, x: number, y: number, len: number, width: number, angle: number): void {
  const dx = (Math.cos(angle) * len) / 2;
  const dy = (Math.sin(angle) * len) / 2;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x - dx, y - dy);
  ctx.lineTo(x + dx, y + dy);
  ctx.stroke();
}

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const k = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + k, y);
  ctx.arcTo(x + w, y, x + w, y + h, k);
  ctx.arcTo(x + w, y + h, x, y + h, k);
  ctx.arcTo(x, y + h, x, y, k);
  ctx.arcTo(x, y, x + w, y, k);
  ctx.closePath();
}

function calmHalf(p: Painter, y: number, keep: number): number {
  const dy = (y - p.cy + 0.08 * p.unit) / (keep * p.unit);
  return dy * dy >= 1 ? 0 : Math.sqrt(1 - dy * dy) * keep * p.unit * 1.08;
}

function night(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, 0x000000, 0.25)));
  g.addColorStop(0.55, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.7)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  for (let i = 0; i < 40; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * p.h * 0.7;
    if (calmAt(p, x, y, 0.9)) continue;
    ctx.fillStyle = rgba(lerpColor(t.rim, t.l, p.rng.float() * 0.5), p.rng.range(0.12, 0.3));
    ctx.beginPath();
    ctx.arc(x, y, p.rng.range(0.6, 1.4), 0, TAU);
    ctx.fill();
  }
}

function jar(p: Painter, t: Tones, x: number, base: number, jw: number, tint: number): void {
  const ctx = p.ctx;
  const jh = jw * 1.3;
  const top = base - jh;
  rounded(ctx, x - jw / 2, top + jw * 0.16, jw, jh - jw * 0.16, jw * 0.3);
  ctx.fillStyle = rgba(lerpColor(t.bg, t.far, 0.8), 0.95);
  ctx.fill();
  ctx.strokeStyle = rgba(t.rim, 0.16);
  ctx.lineWidth = Math.max(1, jw * 0.04);
  ctx.stroke();
  const candy = lerpColor(tint, t.bg, 0.5);
  ctx.fillStyle = rgba(candy, 0.5);
  for (let i = 0; i < 6; i++) {
    ctx.beginPath();
    ctx.arc(x + p.rng.range(-0.3, 0.3) * jw, base - p.rng.range(0.1, 0.52) * jh, jw * p.rng.range(0.09, 0.14), 0, TAU);
    ctx.fill();
  }
  ctx.strokeStyle = rgba(t.rim, 0.14);
  ctx.lineWidth = Math.max(1, jw * 0.05);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x - jw * 0.3, top + jw * 0.42);
  ctx.lineTo(x - jw * 0.3, base - jw * 0.3);
  ctx.stroke();
  const lid = lerpColor(t.far, tint, 0.2);
  rounded(ctx, x - jw * 0.56, top, jw * 1.12, jw * 0.22, jw * 0.11);
  ctx.fillStyle = rgba(lid);
  ctx.fill();
  ctx.strokeStyle = rgba(t.rim, 0.18);
  ctx.lineWidth = Math.max(1, jw * 0.035);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, top - jw * 0.06, jw * 0.11, 0, TAU);
  ctx.fillStyle = rgba(lid);
  ctx.fill();
  ctx.stroke();
}

function shelf(p: Painter, t: Tones, x0: number, x1: number, y: number, jw: number): void {
  const ctx = p.ctx;
  if (x1 - x0 < jw) return;
  const thick = Math.max(3, jw * 0.16);
  ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.35));
  ctx.fillRect(x0, y, x1 - x0, thick);
  ctx.fillStyle = rgba(t.rim, 0.1);
  ctx.fillRect(x0, y, x1 - x0, 1);
  ctx.fillStyle = rgba(t.ink, 0.45);
  ctx.fillRect(x0, y + thick, x1 - x0, thick * 0.8);
  const tints = [t.l, t.a1, t.a2];
  let i = p.rng.int(0, 3);
  let x = x0 + jw * 0.9;
  while (x < x1 - jw * 0.7) {
    const w = jw * p.rng.range(0.82, 1.12);
    if (!calmAt(p, x, y - w * 0.65, 0.95)) jar(p, t, x, y, w, tints[i % 3] ?? t.l);
    i++;
    x += w * 1.15 + jw * p.rng.range(0.3, 0.75);
  }
}

function lollipop(p: Painter, t: Tones, x: number, y: number, r: number, len: number, tint: number): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.3), 0.8);
  stick(ctx, x, y + len / 2, len, Math.max(2, r * 0.14), Math.PI / 2);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fillStyle = rgba(lerpColor(t.far, tint, 0.14));
  ctx.fill();
  ctx.strokeStyle = rgba(t.rim, 0.15);
  ctx.lineWidth = Math.max(1, r * 0.05);
  ctx.stroke();
  ctx.beginPath();
  for (let i = 0; i <= 60; i++) {
    const u = i / 60;
    const a = u * TAU * 2.6;
    const rr = r * 0.88 * u;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.strokeStyle = rgba(lerpColor(t.far, tint, 0.32), 0.45);
  ctx.lineWidth = Math.max(1.5, r * 0.13);
  ctx.stroke();
}

function counter(p: Painter, t: Tones, top: number): void {
  const ctx = p.ctx;
  ctx.fillStyle = rgba(lerpColor(t.ink, t.far, 0.45));
  ctx.fillRect(0, top, p.w, p.h - top);
  ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.5));
  ctx.fillRect(0, top - 2, p.w, 5);
  ctx.fillStyle = rgba(t.rim, 0.14);
  ctx.fillRect(0, top - 2, p.w, 1);
}

function cupcake(p: Painter, t: Tones, c: Cake): void {
  const ctx = p.ctx;
  const { x, base, hw, out, frost } = c;
  const top = base - hw * 0.95;
  const line = Math.max(1, hw * 0.025);
  ctx.fillStyle = rgba(0x000000, 0.35);
  ctx.beginPath();
  ctx.ellipse(x, base + hw * 0.03, hw * 0.95, hw * 0.1, 0, 0, TAU);
  ctx.fill();
  const wrap = lerpColor(t.ink, t.a2, 0.22);
  ctx.beginPath();
  ctx.moveTo(x - hw, top);
  ctx.lineTo(x + hw, top);
  ctx.lineTo(x + hw * 0.78, base);
  ctx.lineTo(x - hw * 0.78, base);
  ctx.closePath();
  ctx.fillStyle = rgba(wrap);
  ctx.fill();
  ctx.strokeStyle = rgba(t.rim, 0.14);
  ctx.lineWidth = line;
  ctx.stroke();
  ctx.strokeStyle = rgba(lerpColor(wrap, t.ink, 0.55), 0.5);
  ctx.lineWidth = Math.max(1, hw * 0.03);
  ctx.beginPath();
  for (let i = 1; i < 8; i++) {
    const u = (i / 8 - 0.5 + p.rng.range(-0.02, 0.02)) * 2 * hw;
    ctx.moveTo(x + u, top);
    ctx.lineTo(x + u * 0.78, base);
  }
  ctx.stroke();
  const tiers = [
    { dy: 0.1, rx: 1.12, ry: 0.3 },
    { dy: 0.42, rx: 0.9, ry: 0.27 },
    { dy: 0.7, rx: 0.64, ry: 0.23 },
  ];
  for (const tier of tiers) {
    const cy = top - tier.dy * hw;
    ctx.beginPath();
    ctx.ellipse(x, cy, tier.rx * hw, tier.ry * hw, 0, 0, TAU);
    ctx.fillStyle = rgba(frost);
    ctx.fill();
    ctx.strokeStyle = rgba(t.rim, 0.12);
    ctx.lineWidth = line;
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(x, cy, tier.rx * hw * 0.84, tier.ry * hw * 0.55, 0, 0.3, Math.PI - 0.3);
    ctx.strokeStyle = rgba(lerpColor(frost, t.rim, 0.22), 0.35);
    ctx.lineWidth = Math.max(1, hw * 0.035);
    ctx.stroke();
  }
  const y0 = top - hw * 0.78;
  ctx.beginPath();
  ctx.moveTo(x - hw * 0.28, y0);
  ctx.quadraticCurveTo(x - hw * 0.1, y0 - hw * 0.3, x - out * hw * 0.06, y0 - hw * 0.42);
  ctx.quadraticCurveTo(x + hw * 0.12, y0 - hw * 0.22, x + hw * 0.28, y0);
  ctx.closePath();
  ctx.fillStyle = rgba(frost);
  ctx.fill();
  ctx.strokeStyle = rgba(t.rim, 0.12);
  ctx.lineWidth = line;
  ctx.stroke();
  const colors = [t.a1, t.a2, t.l, t.rim];
  for (let i = 0; i < 9; i++) {
    const tier = tiers[i % 3] ?? tiers[0]!;
    const sx = x + p.rng.range(-0.7, 0.7) * tier.rx * hw;
    const sy = top - tier.dy * hw + p.rng.range(-0.5, 0.3) * tier.ry * hw;
    ctx.strokeStyle = rgba(lerpColor(colors[i % 4] ?? t.l, frost, 0.35), 0.55);
    stick(ctx, sx, sy, hw * 0.09, Math.max(1.5, hw * 0.035), p.rng.float() * Math.PI);
  }
  const cr = hw * 0.17;
  const chx = x - out * hw * 0.1;
  const chy = top - hw * 1.12;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.a1, 0.35), 0.6);
  ctx.lineWidth = Math.max(1, cr * 0.18);
  ctx.beginPath();
  ctx.moveTo(chx, chy - cr);
  ctx.quadraticCurveTo(chx - out * cr * 0.6, chy - cr * 2, chx - out * cr * 1.4, chy - cr * 2.3);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(chx, chy, cr, 0, TAU);
  ctx.fillStyle = rgba(lerpColor(t.l, t.bg, 0.4), 0.55);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(chx - cr * 0.35, chy - cr * 0.35, cr * 0.22, 0, TAU);
  ctx.fillStyle = rgba(t.rim, 0.35);
  ctx.fill();
}

function macarons(p: Painter, t: Tones, x: number, base: number, r: number): void {
  const ctx = p.ctx;
  const tints = [t.l, t.a1, t.a2];
  let y = base - r * 0.42;
  for (let i = 0; i < 3; i++) {
    const rr = r * (1 - i * 0.12);
    const shell = lerpColor(t.far, tints[i] ?? t.l, 0.2);
    for (const dy of [0.24, -0.24]) {
      ctx.beginPath();
      ctx.ellipse(x, y + dy * rr, rr, rr * 0.3, 0, 0, TAU);
      ctx.fillStyle = rgba(shell);
      ctx.fill();
      ctx.strokeStyle = rgba(t.rim, 0.14);
      ctx.lineWidth = Math.max(1, rr * 0.05);
      ctx.stroke();
    }
    ctx.fillStyle = rgba(lerpColor(t.far, t.rim, 0.28));
    ctx.fillRect(x - rr * 0.88, y - rr * 0.07, rr * 1.76, rr * 0.14);
    y -= rr * 0.78;
  }
}

function bonbon(ctx: CanvasRenderingContext2D, t: Tones, x: number, y: number, r: number, tint: number, angle: number): void {
  const fill = rgba(lerpColor(t.far, tint, 0.35), 0.9);
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = fill;
  ctx.strokeStyle = rgba(t.rim, 0.2);
  ctx.lineWidth = Math.max(1, r * 0.1);
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(s * r * 0.8, 0);
    ctx.lineTo(s * r * 1.7, -r * 0.6);
    ctx.lineTo(s * r * 1.7, r * 0.6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 0.78, 0, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function garland(p: Painter, t: Tones, x0: number, y0: number, x1: number, y1: number, sag: number): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.25), 0.5);
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  for (let i = 0; i <= 24; i++) {
    const s = catenary(x0, y0, x1, y1, sag, i / 24);
    if (i === 0) ctx.moveTo(s.x, s.y);
    else ctx.lineTo(s.x, s.y);
  }
  ctx.stroke();
  const r = Math.min(14, Math.max(6, p.unit * 0.03));
  const tints = [t.l, t.a1, t.a2];
  for (let i = 0; i < 5; i++) {
    const s = catenary(x0, y0, x1, y1, sag, 0.14 + i * 0.18);
    bonbon(ctx, t, s.x, s.y + r * 0.6, r, tints[i % 3] ?? t.l, p.rng.range(-0.4, 0.4));
  }
}

function sprinkles(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  const colors = [t.a1, t.a2, t.l, t.rim];
  const len = Math.max(5, p.unit * 0.026);
  for (const s of spots(p, 16, 1.0, 14, p.unit * 0.08)) {
    if (s.y < p.h * 0.22) continue;
    ctx.strokeStyle = rgba(lerpColor(p.rng.pick(colors), t.bg, 0.3), p.rng.range(0.35, 0.5));
    stick(ctx, s.x, s.y, len, Math.max(2, len * 0.32), p.rng.float() * Math.PI);
  }
}

export function paint(p: Painter, look: World): void {
  const t = tonesOf(look);
  const portrait = p.w / p.h < 0.8;
  night(p, t);
  const jw = Math.min(44, Math.max(16, p.unit * 0.085));
  if (portrait) {
    shelf(p, t, -4, p.w + 4, p.h * 0.28, jw);
  } else {
    for (const y of [p.h * 0.25, p.h * 0.46]) {
      const half = calmHalf(p, y, 0.95);
      const edge = half > 0 ? p.cx - half - 6 : p.w * 0.36;
      shelf(p, t, -4, edge, y, jw);
      shelf(p, t, p.w - edge, p.w + 4, y, jw);
    }
  }
  const top = p.h * (portrait ? 0.885 : 0.87);
  const margin = p.cx - p.unit * 1.05;
  const hw = portrait ? Math.min(p.w * 0.15, p.unit * 0.26) : Math.max(p.unit * 0.16, Math.min(p.unit * 0.28, margin * 0.42));
  const lx = portrait ? hw * 1.12 + p.w * 0.04 : Math.max(hw * 1.14 + 10, Math.min(p.w * 0.12, margin * 0.55));
  const lift = portrait ? 1.3 : 1.6;
  const cakes: Cake[] = [
    { x: lx, base: top, hw, out: -1, frost: lerpColor(t.far, t.ceil, 0.85) },
    { x: p.w - lx, base: top, hw, out: 1, frost: lerpColor(t.ink, t.a1, 0.2) },
  ];
  if (p.level >= 2) {
    for (const c of cakes) {
      const wrapTop = c.base - c.hw * 0.95;
      lollipop(p, t, c.x + c.out * c.hw * 0.5, wrapTop - c.hw * lift, c.hw * 0.42, c.hw * lift, t.l);
      lollipop(p, t, c.x - c.out * c.hw * 0.72, wrapTop - c.hw * (lift - 0.35), c.hw * 0.3, c.hw * (lift - 0.4), t.a1);
    }
  }
  counter(p, t, top);
  for (const c of cakes) cupcake(p, t, c);
  if (p.level >= 3) {
    for (const c of cakes) macarons(p, t, c.x - c.out * c.hw * 1.2, c.base, c.hw * 0.3);
  }
  sprinkles(p, t);
  if (p.level >= 4) {
    const reach = Math.min(p.w * 0.36, p.cx - p.unit * 0.3);
    garland(p, t, -10, p.h * 0.03, reach, -8, p.h * 0.09);
    garland(p, t, p.w - reach, -8, p.w + 10, p.h * 0.03, p.h * 0.09);
  }
  calmCenter(p, t.bg, 0.62, 1.15);
  grain(p, 0.05, true);
  vignette(p, 0x000000, 0.45);
}
